"use client";

import type { Media } from "@mw/shared/payload-types";

import { Button } from "@ui/components/core/Button";
import { easedGradientStops } from "@ui/lib/gradients";
import { clsx } from "clsx";
import { Pause, Play } from "lucide-react";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useState } from "react";

import Muted from "@ui/icons/muted.svg";
import SoundOn from "@ui/icons/sound_on.svg";

import { getVideoUrl } from "@utilities/payload";

export interface VideoPlayerVideo {
  title: string;
  description: string;
  media: Media | string | null;
}

export type VideoPlayerContentProps = {
  video: VideoPlayerVideo;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  isMuted: boolean;
  showControls: boolean;
  inView: boolean | undefined;
  enableOverlay?: boolean;
  /** Custom className for the overlay container (title/description). Defaults to responsive width. */
  overlayClassName?: string;
  /** Object-fit behavior for the video. Defaults to "cover". */
  objectFit?: "cover" | "contain";
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onInteraction: () => void;
  toggleMute: () => void;
};

export function VideoPlayerContent({
  video,
  videoRef,
  isMuted,
  showControls,
  inView,
  enableOverlay,
  overlayClassName = "tw-w-3/4 md:tw-w-1/2",
  objectFit = "cover",
  onPlay = () => {},
  onPause = () => {},
  onEnded = () => {},
  onInteraction,
  toggleMute,
}: VideoPlayerContentProps) {
  const t = useTranslations("ClientKeys.video_rotation_demo");
  const tGlobal = useTranslations("ClientKeys.Globals");
  const [isPlaying, setIsPlaying] = useState(false);

  const videoUrl = video.media ? getVideoUrl(video.media) : null;

  if (!videoUrl) {
    return null;
  }

  const overlayGradient = enableOverlay
    ? `linear-gradient(to bottom, ${easedGradientStops("rgba(0, 0, 0, 0.6)", "rgba(0, 0, 0, 0)")})`
    : undefined;

  const togglePlayPause = () => {
    if (videoRef?.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {
          // Play might fail, but the onPause event will keep state accurate
        });
      }
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay();
  };

  const handlePause = () => {
    setIsPlaying(false);
    onPause();
  };

  return (
    <>
      {/* Full-bleed video - only render when in view */}
      <AnimatePresence>
        {inView && (
          <m.video
            key={videoUrl}
            ref={videoRef}
            src={videoUrl}
            className={clsx(
              "tw-absolute tw-inset-0 tw-w-full tw-h-full",
              objectFit === "contain" ? "tw-object-contain" : "tw-object-cover",
            )}
            muted={isMuted}
            playsInline
            autoPlay
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={onEnded}
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0.6 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          />
        )}
      </AnimatePresence>

      {/* Gradient overlay for improved text readability */}
      {enableOverlay && (
        <div
          className='tw-absolute tw-inset-0 tw-pointer-events-none tw-z-[1] md:tw-h-1/3'
          style={{ background: overlayGradient }}
        />
      )}

      {/* Mobile-only invisible button to show controls on tap */}
      <button
        onClick={onInteraction}
        className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-bg-transparent tw-z-[5] md:tw-hidden'
        aria-label={t("tap_to_show_controls")}
      />

      {/* Title and description */}
      {enableOverlay && (
        <div
          className={clsx(
            "tw-absolute tw-top-4 md:tw-top-8 tw-left-4 md:tw-left-8 tw-z-10 tw-gap-12",
            overlayClassName,
          )}
        >
          <p className='tw-type-xl tw-text-white tw-mb-4'>{video.title}</p>
          <p className='tw-type-base tw-text-white/60'>{video.description}</p>
        </div>
      )}

      {/* Mute/Unmute button - top right, always shown on mobile when muted */}
      <Button
        onClick={toggleMute}
        className={clsx(
          "tw-absolute tw-top-4 md:tw-top-6 tw-right-4 md:tw-right-6 tw-z-10 tw-transition-opacity tw-duration-300",
          "tw-scale-90 md:tw-scale-100",
          !showControls && !isMuted && "md:tw-opacity-100 tw-opacity-0",
        )}
        size='pill'
        variant='elevated'
        aria-label={isMuted ? tGlobal("unmute") : tGlobal("mute")}
      >
        {isMuted ? (
          <Muted className='tw-size-4 md:tw-size-5 tw-text-gray-900' />
        ) : (
          <SoundOn className='tw-size-4 md:tw-size-5 tw-text-gray-900' />
        )}
      </Button>

      {/* Play/Pause button - centered on mobile, bottom left on desktop */}
      <button
        onClick={togglePlayPause}
        className={clsx(
          "tw-absolute tw-z-20 tw-bg-black/20 tw-backdrop-blur-xl tw-text-white tw-hover:bg-black/30 tw-flex tw-items-center tw-justify-center tw-rounded-3xl tw-transition-opacity tw-duration-300",
          "tw-h-8 tw-w-12 md:tw-h-10 md:tw-w-14",
          "tw-top-1/2 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2",
          "md:tw-top-auto md:tw-left-6 md:tw-bottom-6 md:tw-translate-x-0 md:tw-translate-y-0",
          !showControls && "md:tw-opacity-100 tw-opacity-0",
        )}
        aria-label={isPlaying ? tGlobal("pause") : tGlobal("play")}
      >
        {isPlaying ? (
          <Pause className='tw-size-4 md:tw-size-5' fill='currentColor' />
        ) : (
          <Play className='tw-size-4 md:tw-size-5' fill='currentColor' />
        )}
      </button>
    </>
  );
}

export default VideoPlayerContent;
